/**
 * PanelHeader — the content review drawer's full-width top bar: creator
 * avatar/name/status on the left, Approve / Conversation / Share / Close on
 * the right.
 *
 * Split out of ContentReviewPanel.tsx. The share controls live here (and not
 * in the panel) because this bar is their only consumer: it owns both the
 * `useCampaignShare` gate and the `usePublicView` guard that keeps the authed
 * share query unreachable from the public shell.
 */
import { Button } from '../../ui/button';
import { Loader2, MessageCircle, X } from '../../../icons';
import { cn } from '../../../lib/utils';
import { ReviewStatusPill, reviewStatusTone } from '../ReviewStatusPill';
import { UnreadBadge } from '../UnreadBadge';
import { SharePopover } from '../../../campaign-detail/share/SharePopover';
import { useCampaignShare } from '../../../campaign-detail/share/useCampaignShare';
import { purposeForPanel, type ShortShareInput } from '../../../campaign-detail/share/shortLink';
import { usePublicView } from '../../../campaign-detail/publicView';

const APPROVE_SPINNER_SIZE = 12;
const THREAD_ICON_SIZE = 16;
const CLOSE_ICON_SIZE = 18;
/** Fallback share purpose when the panel value maps to none. */
const DEFAULT_SHARE_PURPOSE = 'content';

export type SharePanelKind = 'content-review' | 'content-finalized';

export interface PanelHeaderProps {
  creatorName: string;
  creatorAvatarUrl?: string;
  creatorHandle?: string;
  statusLabel: string;

  canReview: boolean;
  reviewBusy?: boolean;
  isApproved?: boolean;
  onApprove: () => void;

  /** D5: hide the conversation toggle entirely (not merely disable it). */
  hideThreadToggle: boolean;
  threadOpen: boolean;
  onToggleThread: () => void;
  unreadCount?: number;

  /** Platform username for the admin share deep link. */
  shareCreatorUsername?: string;
  sharePanel: SharePanelKind;

  onClose: () => void;
}

function buildShareInput(
  shareCreatorUsername: string | undefined,
  sharePanel: SharePanelKind,
): ShortShareInput | null {
  if (!shareCreatorUsername) return null;
  return {
    kind: 'creator',
    username: shareCreatorUsername,
    purpose: purposeForPanel(sharePanel) ?? DEFAULT_SHARE_PURPOSE,
    // No `sub` here — see ScriptReviewPanel.tsx's shareInput comment
    // (2026-08-05 "one link per review panel" decision): a content grant
    // already returns the full panel from one complete payload.
  };
}

function ApprovedButton() {
  return (
    <Button
      variant="default"
      type="button"
      disabled
      className="inline-flex h-10 items-center gap-1.5 rounded-full bg-green-600 px-5 text-sm font-medium text-white opacity-100 cursor-default"
    >
      Approved
    </Button>
  );
}

function ApproveButton({ reviewBusy, onApprove }: { reviewBusy?: boolean; onApprove: () => void }) {
  return (
    <Button
      variant="default"
      type="button"
      onClick={onApprove}
      disabled={reviewBusy}
      className="inline-flex h-10 items-center gap-1.5 rounded-full bg-foreground px-5 text-sm font-medium text-white transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {reviewBusy ? <Loader2 size={APPROVE_SPINNER_SIZE} className="animate-spin" /> : null}
      Approve
    </Button>
  );
}

function ReviewAction({
  isApproved,
  canReview,
  reviewBusy,
  onApprove,
}: Pick<PanelHeaderProps, 'isApproved' | 'canReview' | 'reviewBusy' | 'onApprove'>) {
  if (isApproved) return <ApprovedButton />;
  if (!canReview) return null;
  return <ApproveButton reviewBusy={reviewBusy} onApprove={onApprove} />;
}

function ThreadToggle({
  threadOpen,
  onToggleThread,
  unreadCount,
}: Pick<PanelHeaderProps, 'threadOpen' | 'onToggleThread' | 'unreadCount'>) {
  return (
    <Button
      variant="outline"
      size="icon"
      type="button"
      onClick={onToggleThread}
      className={cn(
        'relative size-10 shrink-0 rounded-full border p-0',
        threadOpen
          ? 'border-foreground bg-muted text-foreground hover:bg-muted'
          : 'border-border bg-card text-foreground hover:bg-accent',
      )}
      aria-pressed={threadOpen}
      aria-label="Conversation"
    >
      <MessageCircle size={THREAD_ICON_SIZE} />
      {unreadCount ? (
        <span className="absolute -right-1 -top-1">
          <UnreadBadge count={unreadCount} variant="inline" toneClassName="bg-rose-500" />
        </span>
      ) : null}
    </Button>
  );
}

export function PanelHeader({
  creatorName,
  creatorAvatarUrl,
  creatorHandle,
  statusLabel,
  canReview,
  reviewBusy,
  isApproved,
  onApprove,
  hideThreadToggle,
  threadOpen,
  onToggleThread,
  unreadCount,
  shareCreatorUsername,
  sharePanel,
  onClose,
}: PanelHeaderProps) {
  const { canShare, makeShortUrl } = useCampaignShare();
  const { isPublicView } = usePublicView();
  const shareInput = buildShareInput(shareCreatorUsername, sharePanel);

  function getShareUrl() {
    return shareInput ? makeShortUrl(shareInput) : null;
  }

  const avatarStyle = creatorAvatarUrl ? { backgroundImage: `url(${creatorAvatarUrl})` } : undefined;
  const showShare = canShare && !!shareCreatorUsername && !isPublicView;

  return (
    <div className="flex h-[70px] shrink-0 items-center gap-3 border-b border-border-subtle! bg-card px-5">
      <div
        className="size-9 shrink-0 rounded-full border border-border bg-muted bg-cover bg-center"
        style={avatarStyle}
        aria-hidden
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="truncate text-body-sm-regular/5 font-medium text-foreground">{creatorName}</span>
          {statusLabel ? <ReviewStatusPill label={statusLabel} tone={reviewStatusTone(statusLabel)} /> : null}
        </div>
        {creatorHandle && (
          <span className="truncate text-caption-regular/4 font-medium text-muted-foreground">{creatorHandle}</span>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <ReviewAction isApproved={isApproved} canReview={canReview} reviewBusy={reviewBusy} onApprove={onApprove} />
        {!hideThreadToggle && (
          <ThreadToggle threadOpen={threadOpen} onToggleThread={onToggleThread} unreadCount={unreadCount} />
        )}
        {/* `!isPublicView`: keeps SharePopover's authed useShareGrants
            query unreachable from the public shell by construction —
            see the same guard in DrawerHeader. */}
        {showShare && <SharePopover input={shareInput} getUrl={getShareUrl} />}
        {/* Public (no-login) share view: the panel IS the shared
            surface — nothing to close back to, so hide the control. */}
        {!isPublicView && (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onClose}
            className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent"
            aria-label="Close"
          >
            <X size={CLOSE_ICON_SIZE} />
          </Button>
        )}
      </div>
    </div>
  );
}
